import { formatJavaProperties, parseJavaProperties } from './javaProperties.ts';
import { ENTITY_TYPE_NAMES, type Audience } from './model.ts';
import { int, scalar, text, transaction, type SqlConnection, type SqlValue } from './sql.ts';

/**
 * Writing lore entries (docs/ENTRY-EDITOR-PLAN.md section 4.3), as the desktop's
 * `LoreEntryRepository` does: rows are updated in place, only the named columns change,
 * and columns or tables this edition does not know are left alone (schema/README.md,
 * rule 6). Timestamps are ISO-8601 instants, which the desktop reads with `Instant.parse`.
 * Each call is one transaction and also marks the realm modified.
 */

export class EntryEditError extends Error {
  constructor(
    readonly code: 'not_found' | 'locked' | 'invalid' | 'conflict',
    message: string,
  ) {
    super(message);
    this.name = 'EntryEditError';
  }
}

/** The desktop's `AudienceLevel.DEFAULT`, as stored. */
const DEFAULT_AUDIENCE = 'READER';
export const TITLE_MAX = 300;
const LABEL_MAX = 100;
const FIELD_NAME_MAX = 100;
const FIELD_VALUE_MAX = 2000;
const RELATIONSHIP_MAX = 100;
const AUDIENCE_NAME_MAX = 100;
/** `Realm.AUDIENCE_SETTING`. */
export const REALM_AUDIENCE_SETTING = 'audience';
const COLLECTION_NAME_MAX = 200;
const IMAGE_NAME_MAX = 200;
/** docs/ENTRY-EDITOR-SLICE-3-PLAN.md decision 7.2. */
export const IMAGE_MAX_BYTES = 10 * 1024 * 1024;
const AUDIENCE_ROLES = new Set(['OWNER', 'EDITOR', 'CONTRIBUTOR', 'PLAYER', 'READER', 'CUSTOM']);

/** `AudienceLevel.toStorageText`: the role, or `CUSTOM:<name>`. */
export function audienceText(audience: Audience): string {
  if (!AUDIENCE_ROLES.has(audience.role)) throw new EntryEditError('invalid', `Unknown audience: ${audience.role}`);
  if (audience.role !== 'CUSTOM') return audience.role;
  const name = audience.customLabel.replace(/[\r\n]+/g, ' ').trim();
  if (name.length > AUDIENCE_NAME_MAX) throw new EntryEditError('invalid', `An audience name can be at most ${AUDIENCE_NAME_MAX} characters.`);
  return `CUSTOM:${name}`;
}

export interface NewEntry {
  readonly title: string;
  readonly entityType: string;
  /** Only for a custom entry: the type's own name. */
  readonly customTypeName?: string;
  /** Who it's for; Reader when not given, as the desktop's default (Settings > A new entry's audience). */
  readonly audience?: Audience;
}

export interface EntryChanges {
  readonly title?: string;
  readonly entityType?: string;
  readonly customTypeName?: string;
  readonly contentHtml?: string;
  readonly aliases?: readonly string[];
  readonly labels?: readonly string[];
  /** The entry's metadata fields, in order: blank names are dropped, and a repeated name keeps its last value. */
  readonly metadata?: readonly (readonly [string, string])[];
  /** The entry's outgoing relationships: at most one per target, as the desktop allows. */
  readonly links?: readonly EntryLinkChange[];
  /** Who the entry is for: a role, or CUSTOM with its own name. */
  readonly audience?: Audience;
}

export interface EntryLinkChange {
  readonly targetId: string;
  readonly relationshipType: string;
}

function cleanTitle(title: string): string {
  const trimmed = title.trim();
  if (trimmed === '') throw new EntryEditError('invalid', 'An entry needs a title.');
  if (trimmed.length > TITLE_MAX) throw new EntryEditError('invalid', `A title can be at most ${TITLE_MAX} characters.`);
  return trimmed;
}

function cleanType(entityType: string, customTypeName: string): { entityType: string; customTypeName: string } {
  if (!(entityType in ENTITY_TYPE_NAMES)) throw new EntryEditError('invalid', `Unknown entry type: ${entityType}`);
  return { entityType, customTypeName: entityType === 'CUSTOM' ? customTypeName.trim() : '' };
}

/**
 * One per line, as the desktop stores them; blanks, repeats and the title itself dropped,
 * as `LoreEntry` drops them.
 */
function cleanAliases(aliases: readonly string[], title: string): string {
  const own = title.trim().toLowerCase();
  const kept = aliases
    .map((alias) => alias.replace(/[\r\n]+/g, ' ').trim())
    .filter((alias) => alias !== '' && alias.toLowerCase() !== own);
  return [...new Set(kept)].join('\n');
}

/** Trimmed and lower-cased, as `LoreEntry.normalizedLabels` stores them. */
function cleanLabels(labels: readonly string[]): string[] {
  const kept = [...new Set(labels.map((label) => label.trim().toLowerCase()).filter((label) => label !== ''))];
  const long = kept.find((label) => label.length > LABEL_MAX);
  if (long) throw new EntryEditError('invalid', `A label can be at most ${LABEL_MAX} characters.`);
  return kept;
}

/** As the desktop's `collectMetadata`: names trimmed, blank names dropped, the last of a repeated name kept. */
function cleanMetadata(fields: readonly (readonly [string, string])[]): string {
  const kept = new Map<string, string>();
  for (const [rawName, value] of fields) {
    const name = rawName.trim();
    if (name === '') continue;
    if (name.length > FIELD_NAME_MAX) throw new EntryEditError('invalid', `A field name can be at most ${FIELD_NAME_MAX} characters.`);
    if (value.length > FIELD_VALUE_MAX) throw new EntryEditError('invalid', `A field value can be at most ${FIELD_VALUE_MAX} characters.`);
    kept.set(name, value);
  }
  return formatJavaProperties(kept);
}

/** One per target, with the type trimmed and lower-cased as `LoreLink` stores it. */
function cleanLinks(conn: SqlConnection, id: string, links: readonly EntryLinkChange[]): Map<string, string> {
  const kept = new Map<string, string>();
  for (const link of links) {
    const target = link.targetId.trim();
    const type = link.relationshipType.trim().toLowerCase();
    if (target === id) throw new EntryEditError('invalid', 'An entry cannot be linked to itself.');
    if (kept.has(target)) throw new EntryEditError('invalid', 'An entry can be linked to another only once.');
    if (type === '') throw new EntryEditError('invalid', 'A relationship needs a type.');
    if (type.length > RELATIONSHIP_MAX) {
      throw new EntryEditError('invalid', `A relationship type can be at most ${RELATIONSHIP_MAX} characters.`);
    }
    if (int(scalar(conn, 'SELECT COUNT(*) FROM lore_entries WHERE id = ?', [target])) === 0) {
      throw new EntryEditError('invalid', 'A linked entry no longer exists.');
    }
    kept.set(target, type);
  }
  return kept;
}

/**
 * Changes only the link rows that differ. The desktop replaces all of an entry's rows on
 * save; doing that here would drop columns a newer edition adds to `entry_links`.
 */
function writeLinks(conn: SqlConnection, id: string, links: Map<string, string>): void {
  const existing = new Map(
    conn
      .all('SELECT target_entry_id, relationship_type FROM entry_links WHERE source_entry_id = ?', [id])
      .map((r) => [text(r['target_entry_id']), text(r['relationship_type'])] as const),
  );
  for (const target of existing.keys()) {
    if (!links.has(target)) conn.run('DELETE FROM entry_links WHERE source_entry_id = ? AND target_entry_id = ?', [id, target]);
  }
  for (const [target, type] of links) {
    const before = existing.get(target);
    if (before === undefined) {
      conn.run('INSERT INTO entry_links (source_entry_id, target_entry_id, relationship_type) VALUES (?, ?, ?)', [id, target, type]);
    } else if (before !== type) {
      conn.run('UPDATE entry_links SET relationship_type = ? WHERE source_entry_id = ? AND target_entry_id = ?', [type, id, target]);
    }
  }
}

function touchRealm(conn: SqlConnection, now: string): void {
  conn.run('UPDATE realm_metadata SET modified = ?', [now]);
}

export function createEntry(conn: SqlConnection, entry: NewEntry, id: string = crypto.randomUUID(), now: string = new Date().toISOString()): string {
  const title = cleanTitle(entry.title);
  const type = cleanType(entry.entityType, entry.customTypeName ?? '');
  transaction(conn, () => {
    conn.run(
      `INSERT INTO lore_entries
         (id, entity_type, custom_type_name, title, content_html, metadata, audience, locked, encrypted_content, aliases, created, modified)
       VALUES (?, ?, ?, ?, '', '', ?, 0, '', '', ?, ?)`,
      [id, type.entityType, type.customTypeName, title, entry.audience ? audienceText(entry.audience) : DEFAULT_AUDIENCE, now, now],
    );
    touchRealm(conn, now);
  });
  return id;
}

/**
 * Changes an entry. A locked entry's text can't change, but everything else can, as on
 * the desktop, where only the text is locked.
 */
export function updateEntry(conn: SqlConnection, id: string, changes: EntryChanges, now: string = new Date().toISOString()): void {
  transaction(conn, () => {
    const row = conn.all('SELECT entity_type, custom_type_name, title, aliases, locked FROM lore_entries WHERE id = ?', [id])[0];
    if (!row) throw new EntryEditError('not_found', 'That entry no longer exists.');
    if (int(row['locked']) !== 0 && changes.contentHtml !== undefined) {
      throw new EntryEditError('locked', "A locked entry's text can't be edited. Remove the lock first.");
    }

    const columns: string[] = [];
    const values: SqlValue[] = [];
    const set = (column: string, value: SqlValue) => {
      columns.push(`${column} = ?`);
      values.push(value);
    };
    const title = changes.title === undefined ? text(row['title']) : cleanTitle(changes.title);
    if (changes.title !== undefined) set('title', title);
    if (changes.entityType !== undefined || changes.customTypeName !== undefined) {
      const type = cleanType(changes.entityType ?? String(row['entity_type']), changes.customTypeName ?? String(row['custom_type_name']));
      set('entity_type', type.entityType);
      set('custom_type_name', type.customTypeName);
    }
    if (changes.contentHtml !== undefined) set('content_html', changes.contentHtml);
    // A new title can match an alias, which the desktop then drops, so they're checked when either changes.
    if (changes.aliases !== undefined || changes.title !== undefined) {
      set('aliases', cleanAliases(changes.aliases ?? text(row['aliases']).split(/\r\n|\r|\n/), title));
    }
    if (changes.metadata !== undefined) set('metadata', cleanMetadata(changes.metadata));
    if (changes.audience !== undefined) set('audience', audienceText(changes.audience));
    const labels = changes.labels === undefined ? undefined : cleanLabels(changes.labels);
    const links = changes.links === undefined ? undefined : cleanLinks(conn, id, changes.links);
    set('modified', now);
    conn.run(`UPDATE lore_entries SET ${columns.join(', ')} WHERE id = ?`, [...values, id]);

    if (labels !== undefined) {
      conn.run('DELETE FROM entry_labels WHERE entry_id = ?', [id]);
      for (const label of labels) conn.run('INSERT INTO entry_labels (entry_id, label) VALUES (?, ?)', [id, label]);
    }
    if (links !== undefined) writeLinks(conn, id, links);
    touchRealm(conn, now);
  });
}

export interface EntryLock {
  /** The text that was encrypted, or that decrypting gave. */
  readonly plaintext: string;
  /** `encryptContent(plaintext, passphrase)`, the desktop's format. */
  readonly encrypted: string;
}

/**
 * Locks an entry's text, as the desktop's `lockEntryContent` does: the encrypted text is
 * stored, the entry is marked locked, and `content_html` is emptied. The page encrypts, so
 * no passphrase reaches this code. If the text changed after it was encrypted, nothing is
 * locked (`conflict`). SQLite's `secure_delete` is on for the write, so the old text is
 * zeroed instead of staying in the realm file's free space.
 */
export function lockEntry(conn: SqlConnection, id: string, lock: EntryLock, now: string = new Date().toISOString()): void {
  if (lock.encrypted === '') throw new EntryEditError('invalid', 'There is no encrypted text to store.');
  withSecureDelete(conn, () =>
    transaction(conn, () => {
      const row = conn.all('SELECT content_html, locked FROM lore_entries WHERE id = ?', [id])[0];
      if (!row) throw new EntryEditError('not_found', 'That entry no longer exists.');
      if (int(row['locked']) !== 0) throw new EntryEditError('locked', 'That entry is already locked.');
      if (text(row['content_html']) !== lock.plaintext) {
        throw new EntryEditError('conflict', 'The text changed while it was being locked. Try again.');
      }
      conn.run("UPDATE lore_entries SET content_html = '', locked = 1, encrypted_content = ?, modified = ? WHERE id = ?", [
        lock.encrypted,
        now,
        id,
      ]);
      touchRealm(conn, now);
    }),
  );
}

/**
 * Removes an entry's lock for good, as the desktop's `removeLock` does: the decrypted text
 * is restored and the encrypted text cleared. The entry must still hold the encrypted text
 * the page decrypted (`conflict` otherwise).
 */
export function removeLock(conn: SqlConnection, id: string, lock: EntryLock, now: string = new Date().toISOString()): void {
  transaction(conn, () => {
    const row = conn.all('SELECT encrypted_content, locked FROM lore_entries WHERE id = ?', [id])[0];
    if (!row) throw new EntryEditError('not_found', 'That entry no longer exists.');
    if (int(row['locked']) === 0 || text(row['encrypted_content']) !== lock.encrypted) {
      throw new EntryEditError('conflict', 'That entry changed. Open it again and retry.');
    }
    conn.run("UPDATE lore_entries SET content_html = ?, locked = 0, encrypted_content = '', modified = ? WHERE id = ?", [
      lock.plaintext,
      now,
      id,
    ]);
    touchRealm(conn, now);
  });
}

function withSecureDelete<T>(conn: SqlConnection, work: () => T): T {
  const before = int(scalar(conn, 'PRAGMA secure_delete'));
  conn.exec('PRAGMA secure_delete = ON');
  try {
    return work();
  } finally {
    conn.exec(`PRAGMA secure_delete = ${before === 0 ? 'OFF' : 'ON'}`);
  }
}

/**
 * Deletes an entry. Foreign keys are on (`hardenConnection`), so its labels, links,
 * images and collection memberships go with it, and other modules' references to it
 * are cleared, as on the desktop.
 */
export function deleteEntry(conn: SqlConnection, id: string, now: string = new Date().toISOString()): void {
  transaction(conn, () => {
    if (int(scalar(conn, 'SELECT COUNT(*) FROM lore_entries WHERE id = ?', [id])) === 0) {
      throw new EntryEditError('not_found', 'That entry no longer exists.');
    }
    conn.run('DELETE FROM lore_entries WHERE id = ?', [id]);
    touchRealm(conn, now);
  });
}

/** What deleting an entry also removes, for the confirmation (docs/ENTRY-EDITOR-PLAN.md section 4.5). */
export interface DeletionImpact {
  readonly labels: number;
  readonly links: number;
  readonly images: number;
  readonly collections: number;
}

export function deletionImpact(conn: SqlConnection, id: string): DeletionImpact {
  const count = (sql: string, params: readonly SqlValue[] = [id]) => int(scalar(conn, sql, params));
  return {
    labels: count('SELECT COUNT(*) FROM entry_labels WHERE entry_id = ?'),
    links: count('SELECT COUNT(*) FROM entry_links WHERE source_entry_id = ? OR target_entry_id = ?', [id, id]),
    images: count('SELECT COUNT(*) FROM entry_images WHERE entry_id = ?'),
    collections: count('SELECT COUNT(*) FROM entry_collection_members WHERE entry_id = ?'),
  };
}

// --- Images (docs/ENTRY-EDITOR-SLICE-3-PLAN.md section 3.1) -----------------------------

/** The picture types the page accepts, by their first bytes. */
export type ImageType = 'image/png' | 'image/jpeg' | 'image/gif' | 'image/webp' | 'image/bmp';
/** What's stored: what the desktop's JavaFX `Image` reads and isn't oversized. WebP and BMP are converted first. */
export const STORED_IMAGE_TYPES: ReadonlySet<string> = new Set(['image/png', 'image/jpeg', 'image/gif']);

const startsWith = (data: Uint8Array, bytes: readonly number[], at = 0) => bytes.every((b, i) => data[at + i] === b);
const ascii = (text: string) => [...text].map((c) => c.charCodeAt(0));

/** A picture's type from its first bytes, whatever its file is called; `null` for anything else, such as SVG. */
export function imageTypeOf(data: Uint8Array): ImageType | null {
  if (startsWith(data, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'image/png';
  if (startsWith(data, [0xff, 0xd8, 0xff])) return 'image/jpeg';
  if (startsWith(data, ascii('GIF87a')) || startsWith(data, ascii('GIF89a'))) return 'image/gif';
  if (startsWith(data, ascii('RIFF')) && startsWith(data, ascii('WEBP'), 8)) return 'image/webp';
  if (startsWith(data, ascii('BM')) && data.length > 26) return 'image/bmp';
  return null;
}

/** The name as shown: no folders, no line breaks, at most 200 characters. */
function cleanImageName(fileName: string): string {
  const base = fileName.split(/[\\/]/).pop()!.replace(/[\r\n\t]+/g, ' ').trim();
  return (base || 'image').slice(0, IMAGE_NAME_MAX);
}

export interface NewImage {
  readonly fileName: string;
  readonly data: Uint8Array;
}

/**
 * Attaches a picture to an entry, as the desktop's `addImage` does: the bytes as they
 * are, with the type their first bytes show. Only PNG, JPEG and GIF are stored (the page
 * converts WebP and BMP to PNG), at most 10 MB. As on the desktop, the entry's `modified`
 * time doesn't change. `id` and `now` let an undone removal put an image back as it was.
 */
export function addImage(
  conn: SqlConnection,
  entryId: string,
  image: NewImage,
  id: string = crypto.randomUUID(),
  now: string = new Date().toISOString(),
): string {
  const type = imageTypeOf(image.data);
  if (type === null || !STORED_IMAGE_TYPES.has(type)) {
    throw new EntryEditError('invalid', "That isn't a picture Realmery can use (PNG, JPEG, GIF, WebP or BMP).");
  }
  if (image.data.byteLength > IMAGE_MAX_BYTES) throw new EntryEditError('invalid', 'A picture can be at most 10 MB.');
  transaction(conn, () => {
    if (int(scalar(conn, 'SELECT COUNT(*) FROM lore_entries WHERE id = ?', [entryId])) === 0) {
      throw new EntryEditError('not_found', 'That entry no longer exists.');
    }
    conn.run('INSERT INTO entry_images (id, entry_id, file_name, mime_type, data, created) VALUES (?, ?, ?, ?, ?, ?)', [
      id,
      entryId,
      cleanImageName(image.fileName),
      type,
      image.data,
      now,
    ]);
    touchRealm(conn, now);
  });
  return id;
}

export function removeImage(conn: SqlConnection, imageId: string, now: string = new Date().toISOString()): void {
  transaction(conn, () => {
    if (int(scalar(conn, 'SELECT COUNT(*) FROM entry_images WHERE id = ?', [imageId])) === 0) {
      throw new EntryEditError('not_found', 'That picture is already gone.');
    }
    conn.run('DELETE FROM entry_images WHERE id = ?', [imageId]);
    touchRealm(conn, now);
  });
}

// --- Collections (section 3.1) ---------------------------------------------------------

/** Makes a collection, as the desktop's `createCollection` does: Reader audience, and names may repeat. */
export function createCollection(conn: SqlConnection, name: string, id: string = crypto.randomUUID(), now: string = new Date().toISOString()): string {
  const trimmed = collectionName(name);
  transaction(conn, () => {
    conn.run('INSERT INTO entry_collections (id, name, audience, created) VALUES (?, ?, ?, ?)', [id, trimmed, DEFAULT_AUDIENCE, now]);
    touchRealm(conn, now);
  });
  return id;
}

/** A collection's name: line breaks as spaces, trimmed, not blank, and not too long. */
function collectionName(name: string): string {
  const trimmed = name.replace(/[\r\n]+/g, ' ').trim();
  if (trimmed === '') throw new EntryEditError('invalid', 'A collection needs a name.');
  if (trimmed.length > COLLECTION_NAME_MAX) throw new EntryEditError('invalid', `A collection name can be at most ${COLLECTION_NAME_MAX} characters.`);
  return trimmed;
}

/** Renames a collection, by the same rules as making one; names may repeat. */
export function renameCollection(conn: SqlConnection, collectionId: string, name: string, now: string = new Date().toISOString()): void {
  const trimmed = collectionName(name);
  transaction(conn, () => {
    requireCollection(conn, collectionId);
    conn.run('UPDATE entry_collections SET name = ? WHERE id = ?', [trimmed, collectionId]);
    touchRealm(conn, now);
  });
}

/**
 * Deletes a collection, as the desktop's `LoreEntryRepository.deleteCollection`: its
 * entries stay, and only their membership goes (`ON DELETE CASCADE`).
 */
export function deleteCollection(conn: SqlConnection, collectionId: string, now: string = new Date().toISOString()): void {
  transaction(conn, () => {
    requireCollection(conn, collectionId);
    conn.run('DELETE FROM entry_collections WHERE id = ?', [collectionId]);
    touchRealm(conn, now);
  });
}

function requireCollection(conn: SqlConnection, collectionId: string): void {
  if (int(scalar(conn, 'SELECT COUNT(*) FROM entry_collections WHERE id = ?', [collectionId])) === 0) {
    throw new EntryEditError('not_found', 'That collection no longer exists.');
  }
}

/** The collections an entry is in, written as differences so columns a newer edition adds survive. */
export function setEntryCollections(conn: SqlConnection, entryId: string, collectionIds: readonly string[], now: string = new Date().toISOString()): void {
  transaction(conn, () => {
    if (int(scalar(conn, 'SELECT COUNT(*) FROM lore_entries WHERE id = ?', [entryId])) === 0) {
      throw new EntryEditError('not_found', 'That entry no longer exists.');
    }
    const wanted = new Set(collectionIds);
    for (const id of wanted) {
      if (int(scalar(conn, 'SELECT COUNT(*) FROM entry_collections WHERE id = ?', [id])) === 0) {
        throw new EntryEditError('invalid', 'That collection no longer exists.');
      }
    }
    const current = new Set(
      conn.all('SELECT collection_id FROM entry_collection_members WHERE entry_id = ?', [entryId]).map((r) => text(r['collection_id'])),
    );
    for (const id of current) {
      if (!wanted.has(id)) conn.run('DELETE FROM entry_collection_members WHERE collection_id = ? AND entry_id = ?', [id, entryId]);
    }
    for (const id of wanted) {
      if (!current.has(id)) conn.run('INSERT INTO entry_collection_members (collection_id, entry_id) VALUES (?, ?)', [id, entryId]);
    }
    touchRealm(conn, now);
  });
}

/**
 * The realm's own audience, as the desktop's `RealmService.setAudience` (File > Realm
 * Audience…): the `audience` key of the realm's settings, its other settings kept.
 */
export function setRealmAudience(conn: SqlConnection, audience: Audience, now: string = new Date().toISOString()): void {
  const stored = audienceText(audience);
  transaction(conn, () => {
    const settings = parseJavaProperties(text(scalar(conn, 'SELECT settings FROM realm_metadata LIMIT 1')));
    settings.set(REALM_AUDIENCE_SETTING, stored);
    conn.run('UPDATE realm_metadata SET settings = ?, modified = ?', [formatJavaProperties(settings), now]);
  });
}

export function setCollectionAudience(conn: SqlConnection, collectionId: string, audience: Audience, now: string = new Date().toISOString()): void {
  const stored = audienceText(audience);
  transaction(conn, () => {
    requireCollection(conn, collectionId);
    conn.run('UPDATE entry_collections SET audience = ? WHERE id = ?', [stored, collectionId]);
    touchRealm(conn, now);
  });
}
